import React, { useEffect, useState } from "react";
import { api } from "./api";
import { eastern } from "./RecordScreen";
import { elapsed } from "./Authorization";
import { MedStatusBadge } from "./MedStatusBadge";
import type { MedStatus } from "../server/station/med-status";

/**
 * The pending-authorization queue (build plan §9.1 #4): one row per held
 * medication, longest waiting first, with the elapsed time ticking. Everyone
 * sees it. Each row opens the medication's page, where the actions are, by
 * role (Path A: Supervisor; packet, Teams and attach: RN and up): the table
 * fits a 1280 px screen without scrolling sideways (M3 Stage 3 fix 4). Past
 * Config PendingAuthorizationAlertHours, a row is marked amber.
 */

interface Row {
  medKey: string;
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  medication: string;
  source: string;
  controlled: boolean;
  pendingSinceUtc: string;
  authorizationStatus: string;
  status: MedStatus;
  decidedAtUtc: string;
  photos: number;
  attested: boolean;
  packetGenerated: boolean;
  teamsOpened: boolean;
  signedPdfAttached: boolean;
  overAlert: boolean;
}

interface Queue {
  rows: Row[];
  alertHours: number;
  teamsLink: string;
  cities: string[];
  can: { authorizeOnSite: boolean; attach: boolean; packet: boolean };
}

const SOURCE: Record<string, string> = { Parent: "Application", AddedAtCheckin: "Added at check-in", AddedMidweek: "Added mid-week" };
const STATUSES: [string, string][] = [["Pending", "Pending"], ["NotAuthorized", "Not authorized"], ["AuthorizedToday", "Authorized today"]];

/** The steps so far, in short words: "2 photos · attested · packet · Teams · signed PDF". */
export function progressOf(r: Pick<Row, "photos" | "attested" | "packetGenerated" | "teamsOpened" | "signedPdfAttached">): string {
  const steps = [
    r.photos ? `${r.photos} photo${r.photos === 1 ? "" : "s"}` : "",
    r.attested ? "attested" : "",
    r.packetGenerated ? "packet" : "",
    r.teamsOpened ? "Teams" : "",
    r.signedPdfAttached ? "signed PDF" : "",
  ].filter(Boolean);
  return steps.length ? steps.join(" · ") : "—";
}

export function Pending(): React.ReactElement {
  const [status, setStatus] = useState("Pending");
  const [city, setCity] = useState("");
  const [q, setQ] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    void api<{ queue: Queue }>("GET", `/api/authorizations?status=${status}&city=${encodeURIComponent(city)}`).then((r) => (r.ok ? (setQ(r.data.queue), setError(null)) : setError(r.data.error ?? "Could not load.")));
  }, [status, city]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Pending authorizations</h2>
      <p className="mb-3 text-sm text-slate-600">Held medications: none is given until it is authorized. {q && <>Marked amber after {q.alertHours} hours.</>} Open a row for Path A, the provider packet, Teams and attaching the signed PDF.</p>
      <div className="section-card flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="pq-s" className="field-label">Show</label>
          <select id="pq-s" className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        <div>
          <label htmlFor="pq-c" className="field-label">City</label>
          <select id="pq-c" className="field-input" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All cities</option>
            {q?.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="(not assigned)">Not assigned</option>
          </select>
        </div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {q && (
        <div className="section-card">
          {q.rows.length === 0 ? (
            <p className="text-sm text-slate-600">Nothing here.</p>
          ) : (
            <table className="w-full table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[22%]" /><col className="w-[10%]" /><col className="w-[24%]" /><col className="w-[14%]" /><col className="w-[20%]" /><col className="w-[10%]" />
              </colgroup>
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-1 pr-2">Student</th><th className="pr-2">City</th><th className="pr-2">Medication</th><th className="pr-2">{status === "Pending" ? "Waiting" : "Decided"}</th><th className="pr-2">Progress</th><th></th>
                </tr>
              </thead>
              <tbody>
                {q.rows.map((r) => (
                  <tr key={r.medKey} className={`border-b border-slate-100 align-top ${r.overAlert ? "bg-amber-50" : ""}`}>
                    <td className="break-words py-1.5 pr-2"><a className="font-medium text-program underline" href={`/station/${encodeURIComponent(r.personKey)}`}>{r.lastName}, {r.firstName}</a><span className="block text-xs text-slate-500">{r.personKey}</span></td>
                    <td className="break-words pr-2">{r.city || "Not assigned"}</td>
                    <td className="break-words pr-2">
                      {r.medication}
                      <span className="mt-0.5 block"><MedStatusBadge status={r.status} /></span>
                      <span className="block text-xs text-slate-500">{SOURCE[r.source] ?? r.source}{r.controlled ? " · controlled" : ""}</span>
                    </td>
                    <td className="pr-2">
                      {status === "Pending" ? (
                        <span className={r.overAlert ? "font-semibold text-amber-900" : ""}>{r.overAlert && "⚠ "}{elapsed(r.pendingSinceUtc, now)}</span>
                      ) : (
                        eastern(r.decidedAtUtc)
                      )}
                      <span className="block text-xs text-slate-500">since {eastern(r.pendingSinceUtc)}</span>
                    </td>
                    <td className="break-words pr-2 text-xs">{progressOf(r)}</td>
                    <td><a className="text-program underline" href={`/authorizations/${encodeURIComponent(r.medKey)}`}>Open</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}
